import { useEffect } from "react";
import { transactionNoteActions } from "../../../actions/entities/transaction-note.actions";
import { useAppDispatch } from "../../../effects/useAppDispatch";
import { Transaction } from "../../../types/amr-pipeline/models/Transaction";
import { Note, NoteReply, SaveNoteReplayModel, TransactionNote } from "../../../types/note/Note";
import { NoteContent } from "../EditNote";
import { NotesPopup } from "../NotesPopup";
import { useTransactionNotes } from "./useTransactionNotes";
import { useTransactionNoteSave } from "./useTransactionNoteSave";
import { useTransactionNoteReplySave } from "./useTransactionNoteReplySave";
import { ImSubscriptionActionBlocker } from "../../amrPipeline/subscription/ImSubscriptionActionBlocker";
import { TransactionHistoryLink } from "../../amrPipeline/common/TransactionHistoryLink";
import { useMyNotesDelete, useTransactionNoteDelete } from "./useTransactionNoteDelete";
import { useTransactionNoteReplyDelete } from "./useTransactionNoteReplyDelete";
import { stringUtils } from "../../../utils";
import { user } from "../../../user";
import { TransactionType } from "../../../types/amr-pipeline/enums/TransactionType";

interface Props {
    transaction: Transaction
    onClose: () => void;
    onNewNoteAdded?: () => void;
    onNoteDeleted?: (count?: number) => void;
}

export function TransactionNotePopup({ transaction, onClose, onNewNoteAdded, onNoteDeleted }: Props) {
    const dispatch = useAppDispatch();

    const { requestState, notes } = useTransactionNotes(transaction.dealReferenceName, transaction.referenceName);
    const { requestState: noteSaveRequestState, noteSaveCallback } = useTransactionNoteSave(transaction.dealReferenceName, transaction.referenceName);
    const { requestState: replySaveRequestState, replySaveCallback } = useTransactionNoteReplySave();
    const { requestState: noteDeleteRequestState, noteDeleteCallback } = useTransactionNoteDelete();
    const { requestState: myNotesDeleteRequestState, myNotesDeleteCallback } = useMyNotesDelete();
    const { requestState: replyDeleteRequestState, replyDeleteCallback } = useTransactionNoteReplyDelete();

    useEffect(() => {
        return () => {
            dispatch(transactionNoteActions.reset())
        }
    }, [dispatch])

    const handleNoteSave = async (changes: NoteContent, original?: Note) => {
        const noteResult = await noteSaveCallback({
            id: original?.id,
            transactionVersion: transaction.version,
            ...changes
        });

        if (noteResult) {
            dispatch(transactionNoteActions.noteChanged(noteResult));
            if (!original) {
                onNewNoteAdded?.()
            }
            return true;
        }

        return false;
    }

    const handleNoteDelete = async (note: Note) => {
        const isDeleted = await noteDeleteCallback(note.id);

        if (isDeleted) {
            dispatch(transactionNoteActions.noteDeleted(transaction.referenceName, note.id));
            onNoteDeleted?.();
        }
    }

    const handleReplySave = async (reply: SaveNoteReplayModel) => {
        const replyResult = await replySaveCallback(reply);

        if (replyResult) {
            dispatch(transactionNoteActions.noteReplyChanged(transaction.referenceName, replyResult));
            return true;
        }

        return false;
    }

    const handleNoteReplyDelete = async (reply: NoteReply) => {
        const isDeleted = await replyDeleteCallback(reply.id);

        if (isDeleted) {
            dispatch(transactionNoteActions.noteReplyDeleted(transaction.referenceName, reply.noteId, reply.id));
        }
    }

    const handleMyNotesDelete = async () => {
        const myNotesCount = notes.filter(n =>
            stringUtils.equalsIgnoringCase(n.user.email, user.current()?.email ?? '')).length;

        const isDeleted = await myNotesDeleteCallback(transaction.dealReferenceName, transaction.referenceName);

        if (isDeleted) {
            dispatch(transactionNoteActions.myNotesDeleted(transaction.referenceName));
            onNoteDeleted?.(myNotesCount);
        }
    }

    return <NotesPopup
        title={
            <>
                Notes {!!notes.length && `(${notes.length})`}  <span className="name-tag">{transaction.dealLegalName}</span>
            </>
        }
        requestState={requestState}
        noteSaveRequestState={noteSaveRequestState}
        noteReplySaveRequestState={replySaveRequestState}
        noteDeleteRequestState={noteDeleteRequestState}
        noteReplyDeleteRequestState={replyDeleteRequestState}
        myNotesDeleteRequestState={myNotesDeleteRequestState}
        notes={notes}
        renderCustomHeaderContent={note =>
            transaction.type !== TransactionType.amr &&
            <ImSubscriptionActionBlocker>
                <TransactionHistoryLink
                    target="_self"
                    label
                    icon={false}
                    labelText="Trans."
                    referenceName={transaction.referenceName}
                    dealReferenceName={transaction.dealReferenceName}
                    version={(note as TransactionNote).transactionVersion}
                    className="btn-link secondary regular"
                />
            </ImSubscriptionActionBlocker>
        }
        onNoteSave={handleNoteSave}
        onNoteReplySave={handleReplySave}
        onNoteDelete={handleNoteDelete}
        onNoteReplyDelete={handleNoteReplyDelete}
        onMyNotesDelete={handleMyNotesDelete}
        onClose={onClose}
    />
}